import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import mockSync from 'scoreboard/components/mock-sync'
import nestCollection from 'scoreboard/components/nest-collection'
import unwrapModel from 'scoreboard/components/unwrap-model'

/**
 * Model for a Chart Designer Feature (e.g. serie, reference-lines-bands, etc.)
 * @type {any}
 */
const ChartDesignerFeature = Backbone.Model.extend({
	initialize: function (attr, opt = {}) {
		this.data = opt.data
		this.children = new FeatureCollection(this.get('children'))
		nestCollection({
			parent: this,
			parentAttribute: 'children',
			children: this.children
		})
	},

	getLabel: function(options = {}) {
		const {label, type} = this.attributes
		return label || i18n.get(`charts.feature.${type}`)
	},

	isSerie: function() {
		return 'serie' === this.get('type')
	},

	getChildren(predicate = _.constant(true)) {
		return _.filter(_.map(this.children.toArray(), unwrapModel), predicate)
	},

	/**
	 * Returns true if this feature or one of its child features has an error
	 */
	hasError: function () {
		return this.errors().length > 0
	},

	errors () {
		let errors = []
		if (this.get('error')) {
			errors.push(this.get('error'))
		}
		return errors.concat(this.children.toArray().flatMap(c => c.errors()))
	},

	/**
	 * Adaptation of Backbone.Model.toJSON() that leaves out attributes that
	 * don't need to be sent to the server, like error messages and valueSuggestions
	 */
	toPostJSON: function () {
		const f =_.omit(this.toJSON(), 'error', 'children')
		f.children = this.children.map(f => f.toPostJSON())
		return f
	}
})

const FeatureCollection = Backbone.Collection.extend({
	sync: mockSync,
	model: ChartDesignerFeature
})


const tempId = () => _.uniqueId('f')

/**
 * A predicate that returns true if a feature matches the provided id or tempId
 * @returns {Function}
 */
function idPredicate(id, tempId) {
	return f => {
		const {id: fId, tempId: fTempId} = unwrapModel(f)
		return !_.isNil(fId) && fId == id ||
			!_.isNil(fTempId) && fTempId == tempId
	}
}

/**
 * Updates a feature in place, clearing any previously persisted ids and assigning
 * new temporary ids.  Includes all children.
 *
 * @param {Object} feature The unwrapped feature model
 *
 * @param {IdAndTempIdMap} oldRangeIdToNewTempId - a mapping of the original chart's
 * breakdown range IDs to the new chart's range tempIds
 *
 * @param {Function[]} postProcessingOperations
 * An accumulator of functions that should be invoked after all chart features
 * have new ids assigned.  A mapping of oldId -> newTempId is passed.
 */
function assignNewIds(feature, oldRangeIdToNewTempId, postProcessingOperations = []) {
	delete feature.id
	feature.tempId = tempId()

	if (feature.serieSourceId) { // Line sources
		postProcessingOperations.push(idMap => {
			const newIds = idMap.getNewIds(
				{ id: feature.serieSourceId, tempId: feature.serieSourceTempId })
			feature.serieSourceTempId = newIds.tempId
			delete feature.serieSourceId
		})
	}

	const aggregation = _.get(feature, 'source.aggregation')
	if (aggregation) {
		delete aggregation.id
	}
	if (feature.source) {
		delete feature.source.id
	}
	if (feature.forecastModel) {
		delete feature.forecastModel.id
	}
	_.each(feature.children, c => {
		assignNewIds(c, oldRangeIdToNewTempId, postProcessingOperations)
		if (c.parentId) {
			delete c.parentId
			c.tempParentId = feature.tempId
		}
	})

	if (oldRangeIdToNewTempId) {
		_.each(feature.periods, p => {
			const newIds = oldRangeIdToNewTempId.getNewIds(
				{ id: p.breakdownRangeId, tempId: p.breakdownRangeTempId })
			p.breakdownRangeTempId = newIds ? newIds.tempId : null
			delete p.breakdownRangeId
		})
	}
}

function isSerieFieldByIdOrTempId(serie, serieId, serieTempId, aggSortOrder = null) {
	if (!serieId && !serieTempId) return !serie.serieId && !serie.serieTempId
	if (serieId && serie.serieId == serieId) return true
	if (serieTempId && serie.serieTempId == serieTempId) return true
	return false
}

_.extend(ChartDesignerFeature, {
	tempId,
	assignNewIds,
	idPredicate,
	isSerieFieldByIdOrTempId
})

export default ChartDesignerFeature
ChartDesignerFeature.__filename__ = 'charts/model/chart-designer-feature.js'
